/**
 * The rail search's Apps group is TWO fetches merged: marketplace listing
 * channels (`/api/marketplace/channels?root=apps`) and widget posts
 * (`/api/posts?content_type=widget`). A channel row leads because it carries
 * the category; a post that names the same app must fold into it, or the
 * same app shows twice ("Revent / productivity" and "Revent / @robutler").
 *
 * WHY THIS IS NOT A CONTENT-ID CHECK ANY MORE (the Revent e2e run of
 * 2026-09-26/27). The merge folded on the content id alone. A channel's
 * `primaryContentId` is the catalog row today, but until the dev seed
 * repointed it the channel named a FROZEN VERSION artifact while the post
 * named the working row, and the service worker still hands a browser the
 * pre-repoint channel response for a while after (`/api/*` reads are
 * stale-while-revalidate). Two ids for one app, so two rows. An app has more
 * than one name for itself, and all of them are server-derived, so a post
 * folds into a channel when ANY of these hold:
 *
 *   1. the same content id (`channel.primaryContentId === post.widgetContentId`);
 *   2. the channel's widget row was published FROM this post
 *      (`channel.widgetPostId === post.id`, `content.sourcePostId` on the server);
 *   3. the same maker and the same name, case-insensitively. The maker is the
 *      channel's `makerUsername` (the content creator, or the listing's agent
 *      user when the creator is gone) or its `agentUsername`, against the
 *      post's author. Both handles come from `users` rows the server joined,
 *      never from the app's own manifest: `widgetMetadata.author` is
 *      app-declared and a listing could claim any author it liked, which
 *      would let it hide someone else's post of the same name.
 *
 * Pure so it can be pinned without a DOM: `tests/unit/workspaces/
 * search-merge-app-hits.test.ts`.
 */

export type AppHit = {
  id: string;
  title?: string | null;
  thumbnailUrl?: string | null;
  widgetContentId?: string | null;
  installCount?: number | null;
  author?: { username?: string | null } | null;
  /** Marketplace category (channel hits only), e.g. 'games'. */
  category?: string | null;
};

/** A marketplace listing channel (subset of /api/marketplace/channels items). */
export type MarketChannelHit = {
  id: string;
  name: string;
  logoUrl?: string | null;
  primaryContentId?: string | null;
  category?: string | null;
  /** The widget row's `sourcePostId`: the post this listing was published from. */
  widgetPostId?: string | null;
  /** The content creator's username (or the listing's agent user when the creator is gone). */
  makerUsername?: string | null;
  /** The listing's agent user, when it has one (`primaryAgentId`). */
  agentUsername?: string | null;
};

const handle = (v: unknown): string =>
  typeof v === 'string' ? v.trim().replace(/^@+/, '').toLowerCase() : '';
const name = (v: unknown): string => (typeof v === 'string' ? v.trim().toLowerCase() : '');

/** True when the post names the same app as the channel (rules 1 to 3 above). */
export function sameApp(channel: MarketChannelHit, post: AppHit): boolean {
  if (channel.primaryContentId && channel.primaryContentId === post.widgetContentId) return true;
  if (channel.widgetPostId && channel.widgetPostId === post.id) return true;
  const author = handle(post.author?.username);
  if (!author) return false;
  const makers = [handle(channel.makerUsername), handle(channel.agentUsername)].filter(Boolean);
  if (!makers.includes(author)) return false;
  const n = name(channel.name);
  return !!n && n === name(post.title);
}

/**
 * Channel hits first (they carry the category), then every post that names
 * an app no channel already names, capped at `limit`. A channel with no
 * content id cannot be mounted and is dropped; so is a post with none.
 */
export function mergeAppHits(
  channels: readonly MarketChannelHit[],
  posts: readonly AppHit[],
  limit = 6,
): AppHit[] {
  const listed = channels.filter((c) => !!c.primaryContentId);
  const channelHits: AppHit[] = listed.map((c) => ({
    id: c.id,
    title: c.name,
    thumbnailUrl: c.logoUrl ?? null,
    widgetContentId: c.primaryContentId,
    category: c.category ?? null,
  }));
  const postHits = posts.filter(
    (p) => !!p.widgetContentId && !listed.some((c) => sameApp(c, p)),
  );
  return [...channelHits, ...postHits].slice(0, limit);
}
